import { ref, reactive } from 'vue'
import { getClients, getClient } from '../services/centers.js'
import { getProducts, getProductGroups, getPriceListProducts } from '../services/products.js'
import { getOrders, getOrder } from '../services/orders.js'

// ─── Singleton reactive state ─────────────────────────────────────────────────
const centers    = ref([])
const products   = ref([])
const categories = ref([])
const orders     = ref([])
const lastOrders     = reactive({}) // { [centerId]: { date, items, orderId } }
const lastOrderItems = reactive({}) // { [centerId]: { [productId]: qty } }
const loading    = ref(false)
const error      = ref(null)

// Stored so reload helpers can re-use the same params after initial load
let activeChainId    = null
let activeCompanyId  = null
let activePriceListId = null

// ─── Adapters (API shape → App shape) ────────────────────────────────────────

function adaptCenter(c) {
  const a = c.address || {}
  const street = [a.street, a.ext_number].filter(Boolean).join(' ')
  const city   = a.city?.name || ''
  const state  = a.state?.name || ''
  const full   = [street, a.neighborhood, city, state].filter(Boolean).join(', ')
  return {
    id:        c.id,
    name:      c.business_name || c.name || '',
    phone:     c.phone || '',
    address:   full || c.business_name || c.name || '',
    ruta:      c.route?.name || '',
    cadena:    c.chain?.name || '',
    route_id:          c.route?.id ?? c.route_id ?? null,
    route_delivery_id: c.route_delivery?.id ?? c.route_delivery_id ?? null,
    center_id:         c.center?.id ?? c.center_id ?? null,
    latitude:  a.latitude  ?? 0,
    longitude: a.longitude ?? 0,
    lastOrder: null,
  }
}

function adaptProduct(p) {
  // product_unity can be a string or an object { id, name }
  const unit = typeof p.product_unity === 'string'
    ? p.product_unity
    : (p.product_unity?.name || 'Pieza')
  return {
    id:      p.id,
    sku:     p.code_company || p.code || '',
    name:    p.name,
    cat:     p.product_group?.id != null ? String(p.product_group.id) : null,
    unit,
    decimal: !!(p.has_decimal ?? p.decimal),
    price:   p.price || 0,
  }
}

function adaptCategory(g) {
  return { id: String(g.id), name: g.name }
}

// ─── Date helpers ─────────────────────────────────────────────────────────────
function fmtDate(val) {
  if (!val) return ''
  // API returns "DD/MM/YYYY HH:MM" — strip the time part
  return String(val).split(' ')[0]
}

// ─── Parse API collection responses (may be array or wrapped object) ──────────
function toArray(data, key) {
  if (!data) return []
  if (Array.isArray(data)) return data
  if (data[key] && Array.isArray(data[key])) return data[key]
  // AMS sometimes returns { "key": [...] }
  const first = Object.values(data).find(v => Array.isArray(v))
  return first || []
}

// ─── Main composable ──────────────────────────────────────────────────────────
export function useStore() {
  function getProductById(id) {
    // id may be string or number
    return products.value.find(p => p.id == id) || null
  }
  function getCenterById(id) {
    return centers.value.find(c => c.id == id) || null
  }
  function getCategoryById(id) {
    return categories.value.find(c => c.id === String(id)) || null
  }

  async function loadAll(companyId, chainId) {
    activeChainId   = chainId   ?? null  // persist for loadLastOrders re-calls
    activeCompanyId = companyId ?? null  // persist for reloadProducts re-calls
    loading.value   = true
    error.value     = null
    try {
      const clientsParams = chainId ? { chain_id: chainId, per_page: 500 } : { per_page: 500 }

      // 1. Fetch clients list + product groups in parallel
      const [clientsRaw, groupsRaw] = await Promise.all([
        getClients(clientsParams),
        getProductGroups(companyId ? { company_id: companyId } : {}),
      ])

      const clientsList = toArray(clientsRaw, 'clients')
      centers.value = clientsList.map(adaptCenter)

      // 2. Resolve price_list_id from first client detail, then fetch products with real prices
      let productsRaw
      try {
        const firstId = clientsList[0]?.id
        const detail  = firstId ? await getClient(firstId) : null
        activePriceListId = (detail?.client || detail)?.price_list?.id ?? null
        productsRaw = activePriceListId
          ? await getPriceListProducts(activePriceListId)
          : await getProducts(companyId ? { company_id: companyId } : {})
      } catch {
        activePriceListId = null
        productsRaw = await getProducts(companyId ? { company_id: companyId } : {})
      }

      products.value = toArray(productsRaw, 'products').map(adaptProduct)

      const all = { id: 'all', name: 'Todos' }
      categories.value = [all, ...toArray(groupsRaw, 'product_groups').map(adaptCategory)]

      // Load orders to populate OrdersList and lastOrders map per client
      await loadLastOrders()
    } catch (e) {
      error.value = e.message
      console.error('[useStore] loadAll error:', e)
    } finally {
      loading.value = false
    }
  }

  async function loadLastOrders() {
    try {
      const params = { per_page: 50, ...(activeChainId ? { chain_id: activeChainId } : {}) }
      const raw = await getOrders(params)
      const list = toArray(raw, 'orders')

      // Normalise shape: order_status_id → status_id, client.business_name → center_name
      orders.value = list.map(o => ({
        ...o,
        status_id:   o.order_status_id ?? o.status_id ?? null,
        center_name: o.client?.business_name || o.client?.name || '',
        // client may be a nested object OR the API may only return client_id directly
        clientId:    o.client?.id ?? o.client_id ?? null,
      }))

      // Build map: clientId → most recent order (for QuickActions "último pedido")
      const byClient = {}
      for (const o of orders.value) {
        const cid = o.clientId
        if (!cid) continue
        if (!byClient[cid] || new Date(o.ordered_at) > new Date(byClient[cid].ordered_at)) {
          byClient[cid] = o
        }
      }

      for (const [cid, o] of Object.entries(byClient)) {
        lastOrders[cid] = {
          date:    fmtDate(o.ordered_at),
          items:   o.order_details || o.details || [],
          orderId: o.id,
          status:  o.status_id,
        }
      }
    } catch (e) {
      console.warn('[useStore] loadLastOrders error:', e)
    }
  }

  // Re-fetch only the products list (e.g. after a new product is added to the catalog)
  async function reloadProducts() {
    try {
      const productsRaw = activePriceListId
        ? await getPriceListProducts(activePriceListId)
        : await getProducts(activeCompanyId ? { company_id: activeCompanyId } : {})
      products.value = toArray(productsRaw, 'products').map(adaptProduct)
    } catch (e) {
      console.warn('[useStore] reloadProducts error:', e)
    }
  }

  // Fetch order_details for each cid's last order and cache into lastOrderItems
  async function loadLastOrderItems(cids = []) {
    const needed = cids.filter(cid => lastOrders[cid]?.orderId && !lastOrderItems[cid])
    if (!needed.length) return
    await Promise.allSettled(needed.map(async cid => {
      try {
        const res     = await getOrder(lastOrders[cid].orderId)
        const details = res?.order?.order_details || res?.order?.details || res?.details || []
        const map     = {}
        for (const d of details) {
          const pid = d.product_id ?? d.product?.id
          if (pid != null) map[String(pid)] = d.quantity || 0
        }
        lastOrderItems[cid] = map
      } catch {
        lastOrderItems[cid] = {}
      }
    }))
  }

  return {
    centers,
    products,
    categories,
    orders,
    lastOrders,
    lastOrderItems,
    loading,
    error,
    getProductById,
    getCenterById,
    getCategoryById,
    loadAll,
    loadLastOrders,
    loadLastOrderItems,
    reloadProducts,
  }
}
